import React, { useState, useEffect, useRef } from 'react';
import './CreateNote.css';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebSocket } from './WebSocketContext';
import ReactGA from 'react-ga4';

const CreateNote = () => {
  const { socket, clientId, connectionId  } = useWebSocket(); // Get socket and clientId from context
  const clientIdRef = useRef(clientId);
  const [formData, setFormData] = useState({
    emailSubject: '',
    bodyText: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Capture coordinates and address from navigation state (when coming from map)
  const [coordinates, setCoordinates] = useState(null);
  const [address, setAddress] = useState(null); 

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);
  
  useEffect(() => {
    clientIdRef.current = clientId;
  }, [clientId]);

  // Capture coordinates and address from navigation state when coming from map
  useEffect(() => {
    if (location.state) {
      // Check if we have coordinates from map click (use lat_long from backend, fallback to geolat_long for backward compatibility)
      const latLongValue = location.state.lat_long?.value || location.state.geolat_long?.value;
      if (latLongValue) {
        const [lat, lng] = latLongValue.split(',').map(coord => coord.trim());
        setCoordinates({ lat: parseFloat(lat), lng: parseFloat(lng) });
        console.log('Coordinates captured from map:', { lat: parseFloat(lat), lng: parseFloat(lng) });
      }
      
      // Check if we have address from map click
      if (location.state.manualTags?.Address) {
        setAddress(location.state.manualTags.Address);
        console.log('Address captured from map:', location.state.manualTags.Address);
      }
      
      // Pre-populate note name if provided (e.g., from journey location)
      if (location.state.emailSubject) {
        setFormData(prev => ({
          ...prev,
          emailSubject: location.state.emailSubject
        }));
        console.log('Note name pre-populated:', location.state.emailSubject);
      }
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const newErrors = {};
    if (!formData.emailSubject.trim()) newErrors.emailSubject = 'Note Name is required';
    if (!formData.bodyText.trim()) newErrors.bodyText = 'Note Text is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    const currentTimestamp = new Date().toISOString();

    const noteData = {
      emailSubject: formData.emailSubject.trim(),
      urlsPresent: false,
      bodyText: formData.bodyText.trim(),
      urls: [],
      manualTags: {},
      completed: false,  // New notes should be active, not completed
      noteActive: true,  // New notes should be active
    };

    // Add coordinates and address if they were captured from map click
    if (coordinates) {
      noteData.manualTags = {
        ...noteData.manualTags,
        ...(address && { Address: address })
      };
      
      // Add the lat_long field - backend will handle geolocation, but if coordinates are provided from map click, include them
      noteData.aiPromptResults = {
        lat_long: { value: `${coordinates.lat},${coordinates.lng}`, probability: 100.0 }
      };
      
      console.log('Adding coordinates to note:', coordinates);
      console.log('Adding address to note:', address);
      console.log('Note data with coordinates:', JSON.stringify(noteData, null, 2));
    }

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const apiUrl = process.env.REACT_APP_API_URL; 

      console.log('POST payload:', JSON.stringify(noteData, null, 2));

      const response = await axios.post(`${apiUrl}/notes`, noteData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Note created successfully', response.data);
      navigate('/app'); // Navigate back to the main app
    } catch (error) {
      console.error('Error creating note', error);
      setErrors({ submit: 'Failed to create note. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/app');
  };

  return (
    <div className="create-note-page">
      <div className="create-note-container">
        <div className="create-note-header">
          <h1>Create New Note</h1>
          <p className="create-note-subtitle">Add a new note to your collection</p>
          {coordinates && (
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginTop: '5px',
              display: 'none' // Hidden from user but can be made visible for debugging
            }}>
              📍 Location captured: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
              {address && ` - ${address}`}
            </div>
          )}
        </div>

        <div className="create-note-form-container">
          <form onSubmit={handleSubmit} className="create-note-form">
            <div className="form-group">
              <label htmlFor="emailSubject" className="form-label">
                Note Name
              </label>
              <input
                type="text"
                id="emailSubject"
                name="emailSubject"
                value={formData.emailSubject}
                onChange={handleInputChange}
                className={`form-input ${errors.emailSubject ? 'form-input-error' : ''}`}
                placeholder="Enter note name..."
                disabled={isSubmitting}
              />
              {errors.emailSubject && (
                <span className="error-message">{errors.emailSubject}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="bodyText" className="form-label">
                Note Text
              </label>
              <textarea
                id="bodyText"
                name="bodyText"
                value={formData.bodyText}
                onChange={handleInputChange}
                className={`form-textarea ${errors.bodyText ? 'form-textarea-error' : ''}`}
                placeholder="Enter your note content..."
                rows={8}
                disabled={isSubmitting}
              />
              {errors.bodyText && (
                <span className="error-message">{errors.bodyText}</span>
              )}
            </div>

            {errors.submit && (
              <div className="error-banner">
                <span className="error-message">{errors.submit}</span>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Note'}
              </button>
            </div>
          </form>

          <div className="create-note-info">
            <div className="info-card">
              <div className="info-icon">
                <i className="fa fa-info-circle"></i>
              </div>
              <div className="info-content">
                <h3>Processing Time</h3>
                <p>Note creation may take up to 1 minute for AI processing and categorization.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNote;
